import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import 'moment/locale/is';
import { TravelExpenseResults } from 'api/services/TravelExpenses';
import {
  Box,
  ButtonGroup,
  Stack,
  Tooltip,
  Text as TextComponent,
} from '@chakra-ui/react';
import {
  IconChatbox,
  IconDocument,
  IconDownload,
  IconEdit,
  IconTrash,
} from 'components/Icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { downloadFileFromBase64 } from 'utils/base64ToBlob';
import { Button } from '@landsvirkjun/react.common';
import { Popover } from 'components/Popover/Popover';
import { Link } from '@chakra-ui/react';

const formatNumberWithDotSeparator = (number: number): string => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

interface ActionsCellProps {
  itemId: string | undefined;
  handleDeleteCostLine: ((id: string | undefined) => void) | undefined;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  itemId,
  handleDeleteCostLine,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');
  const adminPath = location?.pathname?.startsWith('/admin/') ? '/admin' : '';

  return (
    <Popover
      placement="bottom-start"
      useContextMenu
      bodyChildren={
        <Stack direction="column">
          <Stack direction="row">
            <Box as="div" mb={3}>
              <Link
                display="flex"
                onClick={(): void =>
                  navigate(
                    `${adminPath}/ferdabeidnir/ferdabeidni/preview?id=${id}&action=kostnadur&itemId=${itemId}`,
                    {
                      state: { hasActions: true },
                    }
                  )
                }
              >
                <IconEdit style={{ cursor: 'pointer', marginRight: 10 }} />
                Breyta kostnaðarlið
              </Link>
            </Box>
          </Stack>
          <Box as="div">
            <Link
              display="flex"
              onClick={(): void => handleDeleteCostLine?.(itemId)}
            >
              <IconTrash
                style={{ cursor: 'pointer', marginRight: 10 }}
                width={20}
              />
              Eyða kostnaðarlið
            </Link>
          </Box>
        </Stack>
      }
    />
  );
};

export const columns = (
  handleDeleteAttachment?: (doc: string | undefined, id: string) => void,
  isDeletingAttachment?: boolean,
  handleDeleteCostLine?: (id: string | undefined) => void
): ColumnDef<TravelExpenseResults>[] => {
  return [
    {
      accessorKey: 'costType',
      header: () => <span>Kostnaður</span>,
      accessorFn: (row) => row,
      cell: ({ getValue }): JSX.Element => {
        const { costType, carbonEmission, comment } =
          getValue() as TravelExpenseResults;
        return (
          <Box display="flex">
            {costType}
            {carbonEmission !== 0 && (
              <Tooltip label="Kolefnisjöfnun flugs">
                <span style={{ color: 'gray', fontSize: '90%' }}>
                  {' '}
                  - ({carbonEmission} CO₂)
                </span>
              </Tooltip>
            )}
            {comment && (
              <Tooltip
                placement="top"
                bg="bg-default"
                color="font-light"
                fontSize={15}
                fontWeight={400}
                padding="20px 25px 25px"
                boxShadow=" 0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 8px 20px 0 rgba(0, 0, 0, 0.15)"
                label={
                  <>
                    <TextComponent>
                      <strong>Athugasemd:</strong>
                    </TextComponent>
                    {comment}
                  </>
                }
              >
                <IconChatbox
                  width={20}
                  cursor="pointer"
                  style={{ marginLeft: 10 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: 'paymentMethodDescription',
      header: () => <span>Greiðsluháttur</span>,
    },

    {
      accessorKey: 'amount',
      header: () => <span>Upphæð</span>,
      accessorFn: (row) => row,
      cell: ({ getValue }): JSX.Element => {
        const { amount, currency } = getValue() as TravelExpenseResults;
        return (
          <>
            {formatNumberWithDotSeparator(amount)} {currency}
          </>
        );
      },
    },
    {
      accessorKey: 'attachments',
      accessorFn: (row) => row,
      header: () => <span>Viðhengi</span>,
      cell: ({ getValue }): JSX.Element => {
        const { attachments, id } = getValue() as TravelExpenseResults;
        return (
          <Box display="flex" flexDir="column" cursor="pointer">
            {attachments?.map((attachment) => (
              <Box display={'flex'} flexDir="row" mb={1} key={attachment.name}>
                <Popover
                  // key={attachment.name}
                  placement="bottom"
                  triggerIcon={
                    <Box display={'flex'} flexDir="row">
                      <IconDocument width={20} />
                      <span style={{ marginLeft: '5px' }}>
                        {attachment.name}
                      </span>
                    </Box>
                  }
                  bodyChildren={attachment.name}
                  footerChildren={
                    <ButtonGroup size="sm" spacing={10}>
                      <Button
                        variant="warning"
                        rightIcon={<IconTrash width={15} />}
                        onClick={(): void =>
                          handleDeleteAttachment?.(attachment.documentId, id)
                        }
                        isLoading={isDeletingAttachment}
                      >
                        Eyða viðhengi
                      </Button>
                      <Button
                        variant="primary"
                        rightIcon={<IconDownload width={15} />}
                        onClick={(): void =>
                          downloadFileFromBase64(
                            attachment.fileContents,
                            attachment.fileName
                          )
                        }
                      >
                        Sækja viðhengi
                      </Button>
                    </ButtonGroup>
                  }
                />
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      accessorKey: 'context-menu',
      header: () => (
        <Box display="flex" marginLeft="auto">
          Aðgerðir
        </Box>
      ),
      accessorFn: (row) => row,
      cell: ({ getValue }): JSX.Element | null => {
        const { id: itemId } = getValue() as TravelExpenseResults;
        return (
          <ActionsCell
            itemId={itemId}
            handleDeleteCostLine={handleDeleteCostLine}
          />
        );
      },
    },
  ];
};
